import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { toast } from "react-toastify";



const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Handle sending OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/forget/password/send/otp`,
        { email: email },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Response sending OTP:", response);

      if (response.status === 200) {
        setOtpSent(true);
        setError('');
      } else {
        setError(response.data.message || 'Failed to send OTP');
      }
    } catch (err) {
      setError('User is not Exist, Please Enter your Email');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle OTP verification
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setError('');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/verify/forget/password/otp`,
        { email, otp },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Response verifying OTP:", response);
      if (response.status === 200) {
        setShowPasswordModal(true);
        setError('');
      } else {
        setError(response.data.message || 'Invalid OTP');
      }
    } catch (err) {
      setError('Wrong OTP. Please try again.');
    } finally {
      setVerifying(false);
    }
  };


  // Handle password reset
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setResetting(true);
    setError('');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/set/new/password`,
        { email, newPassword, confirmPassword },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Response resetting password:", response);
      if (response.status === 200) {
        toast.success('Password reset successful! Please login with your new password.');
        setShowPasswordModal(false);
        setTimeout(() => {
          navigate('/login');
        }, 1500); // Wait for toast to show
      } else {
        setError(response.data.message || 'Failed to reset password');
      }
    } catch (err) {
      setError('Wrong OTP. Please try again.');
    } finally {
      setResetting(false);
    }

  };

  // Resend OTP function
  const handleResendOtp = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/resend/otp`,
        { email },
        {
          headers: {
            "Content-Type": "application/json",

          },
        }
      );
      console.log("Response resending OTP:", response);
      if (response.status === 300) {
        setError('OTP resent successfully');
      } else {
        setError(response.data.message || 'Failed to resend OTP');
      }
    } catch (err) {
      setError('Wrong OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 mt-16 sm:mt-0">
      <div className="w-full max-w-5xl bg-white rounded-[2rem] shadow-2xl shadow-indigo-200/50 overflow-hidden flex flex-col lg:flex-row border border-slate-100 min-h-[550px]">
        {/* Left Side - Company Branding */}
        <div className="hidden lg:block lg:w-5/12 bg-gradient-to-br from-indigo-700 via-blue-600 to-indigo-900 p-10 relative overflow-hidden">
          {/* Abstract Background Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full blur-3xl -mr-20 -mt-20"></div>
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-indigo-400 opacity-20 rounded-full blur-2xl -ml-10 -mb-10"></div>

          <div className="relative z-10 h-full flex flex-col justify-center items-center text-white">
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="w-16 h-16 mb-6 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl flex items-center justify-center shadow-lg"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </motion.div>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <h1 className="text-3xl font-[1000] tracking-tight mb-3 drop-shadow-sm uppercase">Secure Recovery</h1>
              <p className="text-indigo-100 font-bold tracking-wide text-sm opacity-90">
                Regain access to your seamless experience.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="w-full lg:w-7/12 p-8 sm:p-12 flex flex-col justify-center bg-white relative">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-md mx-auto"
          >
            <div className="mb-8 text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl font-[1000] text-slate-900 tracking-tight mb-2 uppercase">
                Forgot Password
              </h1>
              <div className="w-12 h-1.5 bg-indigo-600 rounded-full mb-4 mx-auto lg:mx-0"></div>
              <p className="text-sm font-bold text-slate-500 tracking-wide">Enter your email to receive a verification code</p>
            </div>

            <form onSubmit={otpSent ? handleVerifyOtp : handleSendOtp} className="space-y-5">
              {error && !showPasswordModal && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl text-xs font-bold text-center"
                >
                  {error}
                </motion.div>
              )}

              <div className="space-y-1.5">
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1">
                  Email Address
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      className="text-slate-400 group-focus-within:text-indigo-500 transition-colors"
                    />
                  </div>
                  <input
                    type="email"
                    required
                    className="block w-full pl-10 pr-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-indigo-50 focus:border-indigo-500 outline-none transition-all text-sm font-bold text-slate-900 disabled:opacity-60 disabled:bg-slate-100"
                    placeholder="Enter Your Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={otpSent}
                  />
                </div>
              </div>

              {otpSent && (
                <div className="space-y-1.5">
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1">
                    Enter Verification OTP
                  </label>
                  <div className="relative group">
                    <input
                      type="text"
                      required
                      className="block w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-indigo-50 focus:border-indigo-500 outline-none transition-all text-sm font-bold text-slate-900"
                      placeholder="Enter OTP Code"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                    />
                  </div>
                </div>
              )}

              <div className="pt-2">
                {!otpSent ? (
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full flex justify-center items-center py-3.5 px-4 rounded-xl shadow-lg shadow-indigo-200/50 text-sm font-black text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 active:scale-[0.98] transition-all uppercase tracking-widest disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Sending OTP...' : 'Send OTP'}
                  </button>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="submit"
                      disabled={verifying}
                      className="w-full flex justify-center items-center py-3.5 px-4 rounded-xl shadow-lg shadow-emerald-200/50 text-xs sm:text-sm font-black text-white bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 active:scale-[0.98] transition-all uppercase tracking-widest disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {verifying ? 'Verifying...' : 'Verify OTP'}
                    </button>
                    <button
                      type="button"
                      disabled={isLoading}
                      onClick={handleResendOtp}
                      className="w-full flex justify-center items-center py-3.5 px-4 rounded-xl text-xs sm:text-sm font-black text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border-2 border-indigo-100 hover:border-indigo-200 active:scale-[0.98] transition-all uppercase tracking-widest disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      Resend OTP
                    </button>
                  </div>
                )}
              </div>

              <div className="text-center mt-6">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-slate-500 hover:text-indigo-600 text-xs font-black uppercase tracking-widest transition-colors"
                >
                  ← Back to Login
                </button>
              </div>
            </form>
          </motion.div>

          {/* Password Reset Modal */}
          {showPasswordModal && (
            <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"></div>
              <div className="bg-white rounded-[2rem] shadow-2xl shadow-indigo-500/20 p-8 w-full max-w-sm relative animate-fadeIn border border-slate-100">
                <div className="flex flex-col items-center mb-6">
                  <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <h3 className="text-2xl font-[1000] mb-1 text-center text-slate-800 uppercase tracking-tight">OTP Verified!</h3>
                  <p className="text-slate-500 text-center text-xs font-bold uppercase tracking-wide">Set your new secure password.</p>
                </div>
                {error && (
                  <div className="bg-red-50 text-red-700 font-bold p-3 rounded-xl text-xs text-center mb-4 border border-red-100">{error}</div>
                )}
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <input
                    type="email"
                    required
                    className="block w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-indigo-50 focus:border-indigo-500 outline-none transition-all text-sm font-bold text-slate-900"
                    placeholder="Confirm Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <div className="relative group">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      required
                      autoFocus
                      className="block w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-indigo-50 focus:border-indigo-500 outline-none transition-all text-sm font-bold text-slate-900 pr-10"
                      placeholder="New Password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-3 flex items-center text-slate-400 group-focus-within:text-indigo-500 hover:text-indigo-600 transition-colors"
                      tabIndex={-1}
                      onClick={() => setShowNewPassword((prev) => !prev)}
                    >
                      <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} className="text-sm" />
                    </button>
                  </div>
                  <div className="relative group">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      className="block w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-indigo-50 focus:border-indigo-500 outline-none transition-all text-sm font-bold text-slate-900 pr-10"
                      placeholder="Confirm New Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-3 flex items-center text-slate-400 group-focus-within:text-indigo-500 hover:text-indigo-600 transition-colors"
                      tabIndex={-1}
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                    >
                      <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} className="text-sm" />
                    </button>
                  </div>
                  <button
                    type="submit"
                    disabled={resetting}
                    className="w-full flex justify-center items-center py-3.5 px-4 mt-2 rounded-xl shadow-lg shadow-indigo-200/50 text-sm font-black text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 active:scale-[0.98] transition-all uppercase tracking-widest disabled:opacity-70"
                  >
                    {resetting ? 'Resetting...' : 'Reset Password'}
                  </button>
                </form>
                <button
                  type="button"
                  className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                  onClick={() => setShowPasswordModal(false)}
                  aria-label="Close"
                >
                  <span className="text-xl leading-none font-bold">&times;</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;